/**
 * Quais documentos entram no aviso da tela inicial.
 *
 * Papel: aqui fica `apurarVencimentosDeDocumentos`, que mapeia cada documento
 * para o formato do aviso e entrega a `apurarVencimentos`, de
 * `shared/lib/vencimento.ts`, onde mora a regra da janela.
 *
 * Como usar: este arquivo sabe apenas o que é de documento — o rótulo que a
 * linha leva no aviso, que é o `tipo` como foi gravado, a pessoa escrita por
 * `formatarPessoa`, a mesma forma da `TabelaDocumentos`, e a rota onde a linha
 * se resolve. A janela é `JANELA_DE_VENCIMENTO_EM_DIAS`, de `shared/`; os
 * planos de saúde não a usam, porque entram no aviso só depois de vencidos,
 * pedindo a foto nova da carteirinha (`modules/saude/vencimentos.ts`). A
 * contagem de dias e o descarte de quem não tem validade também são de
 * `shared/`.
 *
 * O `status` não entra na conta, e a consequência é deliberada: um documento
 * marcado `nao_localizado` ou `nao_aplicavel` que tenha `validade` preenchida
 * **aparece no aviso**. É o comportamento que se quer — o documento que ninguém
 * acha e que está para vencer é o caso mais urgente da lista, não o menos.
 */
import { type Documento } from '@concierge/shared';

import { formatarPessoa } from '@/shared/lib/formato';
import {
  JANELA_DE_VENCIMENTO_EM_DIAS,
  type ItemVencendo,
  apurarVencimentos,
} from '@/shared/lib/vencimento';

/**
 * Apura os documentos que entram no aviso.
 *
 * @param documentos - Os documentos como a API os devolve.
 * @param hojeIso - A data de hoje em `AAAA-MM-DD`, de quem chama.
 * @returns Os itens a anunciar, na ordem em que vieram; quem ordena é o
 *   `PopupVencimentos`.
 */
export function apurarVencimentosDeDocumentos(
  documentos: readonly Documento[],
  hojeIso: string,
): ItemVencendo[] {
  return apurarVencimentos(
    documentos.map((documento) => ({
      id: documento.id,
      referente: formatarPessoa(documento.pessoa),
      rotulo: documento.tipo,
      validade: documento.validade,
      destino: '/documentos',
    })),
    hojeIso,
    JANELA_DE_VENCIMENTO_EM_DIAS,
  );
}
